import { useState, useEffect, useRef, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "./authConfig";
import { readSummitFile, writeSummitFile } from "./summitService";

// useSummitSync.js
// Deep merge: default provides structure, remote overwrites leaf values.
// New fields added to defaults appear on first load without wiping saved data.
function deepMergeDefaults(defaults, saved) {
  if (typeof defaults !== "object" || defaults === null) return saved;
  if (typeof saved !== "object" || saved === null) return saved;
  const result = Object.assign({}, defaults);
  Object.keys(saved).forEach(function(k) {
    if (k in defaults && typeof defaults[k] === "object" && defaults[k] !== null && !Array.isArray(defaults[k])) {
      result[k] = deepMergeDefaults(defaults[k], saved[k]);
    } else {
      result[k] = saved[k];
    }
  });
  return result;
}

export default function useSummitSync(key, defaultData, projectConfig) {
  const { instance, accounts } = useMsal();
  const [data, setData] = useState(defaultData);
  const [saveStatus, setSaveStatus] = useState("loading");
  const [loaded, setLoaded] = useState(false);
  const debounceRef = useRef(null);
  const latestDataRef = useRef(defaultData);

  const getToken = useCallback(async function() {
    const res = await instance.acquireTokenSilent({
      ...loginRequest,
      account: accounts[0],
    });
    return res.accessToken;
  }, [instance, accounts]);

  // Load on mount
  useEffect(function() {
    async function load() {
      if (!key) {
        latestDataRef.current = defaultData;
        setData(defaultData);
        setSaveStatus("preview");
        setLoaded(true);
        return;
      }
      setSaveStatus("loading");
      try {
        const token = await getToken();
        const remote = await readSummitFile(key, token, projectConfig);
        if (remote && Object.keys(remote).length > 0 && !remote.test) {
          const merged = deepMergeDefaults(defaultData, remote);
          setData(merged);
          latestDataRef.current = merged;
        } else {
          // File does not exist or is a test file - use defaultData
          latestDataRef.current = defaultData;
        }
        setSaveStatus("saved");
      } catch (err) {
        console.error("Summit load failed:", err);
        setSaveStatus("offline");
        latestDataRef.current = defaultData;
      } finally {
        setLoaded(true);
      }
    }
    load();
  }, [key, projectConfig]);

  // Debounced write
  const save = useCallback(function(newData) {
    latestDataRef.current = newData;
    if (!key) { setSaveStatus("preview"); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setSaveStatus("unsaved");
    debounceRef.current = setTimeout(async function() {
      setSaveStatus("saving");
      try {
        const token = await getToken();
        await writeSummitFile(key, latestDataRef.current, token, projectConfig);
        setSaveStatus("saved");
      } catch (err) {
        console.error("Summit save failed key:", key, err.message);
        setSaveStatus("error");
      }
    }, 1500);
  }, [key, getToken, projectConfig]);

  // Update function - updates state and triggers save
  const update = useCallback(function(updater) {
    setData(function(prev) {
      const next = typeof updater === "function" ? updater(prev) : updater;
      latestDataRef.current = next;
      save(next);
      return next;
    });
  }, [save]);

  // Force save now
  const forceSave = useCallback(async function() {
    if (!key) return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setSaveStatus("saving");
    try {
      const token = await getToken();
      await writeSummitFile(key, latestDataRef.current, token, projectConfig);
      setSaveStatus("saved");
    } catch (err) {
      console.error("Force save failed:", err);
      setSaveStatus("error");
    }
  }, [key, getToken, projectConfig]);

  return { data, update, saveStatus, loaded, forceSave };
}
